import * as w from '@packages/systems/wfdl';
import * as wast from '@packages/systems/wfdl-ast';
import {TDateTime} from '@packages/systems/plugins/DateTime';

import {$, _$, func, tyfun} from '@packages/utilities/wfdl-helpers';
import * as wq from '@packages/systems/wfdl-Qualified';
import {
  TOperand,
  TConditionalUnion,
} from '@packages/systems/plugins/Conditional';
import {TBoolean} from '@packages/systems/plugins-utils/Boolean';

import {increment} from '@packages/utilities/string-utils';
import {TEffect} from '@packages/systems/plugins/Effect';
import {TJson} from '@packages/systems/plugins/Json';
import {formFieldToType, choicesToType} from './formFieldToType';
import {generateFormParser} from './generateFormParser';
import type {ComponentForms} from '@packages/systems/flows/types';
import {formDataFieldsType} from './formDataFieldsType';

export function makeFormPluginValues(componentForms: ComponentForms): {
  [key: string]: w.EnvTypes.TypedValue;
} {
  const result: Record<string, w.EnvTypes.TypedValue> = {};
  const namesMap = new Map();

  for (const componentId in componentForms) {
    // @ts-expect-error - TS2339 - Property 'forms' does not exist on type 'ComponentForm | undefined'. | TS2339 - Property 'title' does not exist on type 'ComponentForm | undefined'.
    const {forms, title} = componentForms[componentId];

    for (const form of forms) {
      const fields = formFieldToType(form);

      const titleFormKey = generateTitleFormName(title, form.name);

      const {titleAndFormName, formNames} = generateFormName({
        title,
        name: form.name,
        formNames: namesMap.get(titleFormKey) || [],
      });

      namesMap.set(titleFormKey, formNames);

      result[titleAndFormName] = {
        envType: {
          dataType: wast.TGeneric({
            arg: 'error',
            body: wast.functionOf(
              formDataFieldsType,
              TEffect(
                wast.TUnion(
                  {
                    'Missing form field': wast.TText,
                    'Failed parsing from Json': TJson,
                    'Form Field Parse Error': wast.TText,
                  },
                  wast.TTypeReference('error')
                ),
                wast.TRecord({
                  'Form fields': wast.TRecord(fields),
                  Timestamp: TDateTime,
                })
              )
            ),
          }),
        },

        expression: generateFormParser(form),
      };

      // @ts-expect-error - TS7006 - Parameter 'formField' implicitly has an 'any' type.
      form.fields.forEach((formField) => {
        if (
          formField.fieldType.tag === 'Radio' ||
          (formField.fieldType.tag === 'Select' &&
            !formField.fieldType.val.allowMultiple)
        ) {
          const choices = formField.fieldType.val.choices;
          const fieldKey = `${titleAndFormName}: ${formField.fieldName}`;
          const fieldType = fields[formField.fieldName];

          // @ts-expect-error - TS2347 - Untyped function calls may not accept type arguments.
          const optionCases = choices.reduce<{[key: string]: wast.Expression}>(
            // @ts-expect-error - TS7006 - Parameter 'acc' implicitly has an 'any' type. | TS7006 - Parameter 'choice' implicitly has an 'any' type.
            (acc, choice) => {
              acc[choice] = func(['_'], wast.EText(choice));
              return acc;
            },
            {}
          );

          result[`${fieldKey} To Text`] = {
            envType: {
              // @ts-expect-error - TS2322 - Type 'Type | undefined' is not assignable to type 'Type'.
              dataType: tyfun([fieldType], wast.TText),
              exported: 'visible',
              implicit: 'prefilledArgs',
            },
            expression: func(
              [wq.identifier('field option')],
              formField.required
                ? wast.ECase({
                    value: $('field option'),
                    cases: optionCases,
                  })
                : wast.ECase({
                    value: $('field option'),
                    cases: {
                      Some: func(
                        ['field'],
                        wast.ECase({value: $('field'), cases: optionCases})
                      ),
                      None: func(['_'], wast.EText('')),
                    },
                  })
            ),
          };

          // Creates a dynamic `Conditional` function unique to the field which expresses field
          // choices as a `TConditionalUnion`, exposing the field as an available argument
          // to the Conditional plugin's `Equals` function, used by the Conditional Block.
          result[`${fieldKey} Conditional`] = {
            envType: {
              dataType: formField.required
                ? // @ts-expect-error - TS2322 - Type 'Type | undefined' is not assignable to type 'Type'. | TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
                  tyfun([fieldType], TOperand(TConditionalUnion(fieldType)))
                : // When the field is optional, a fallback for `True` / `False`
                  // should be configured for the conditional rule.
                  tyfun(
                    [
                      wast.TTypeApplication({
                        con: wast.TTypeConstructor(
                          wq.qualify(
                            wq.pluginName('Option'),
                            wq.typeName('WithFallbackLabel')
                          )
                        ),
                        arg: TBoolean,
                      }),
                      // @ts-expect-error - TS2322 - Type 'Type | undefined' is not assignable to type 'Type'.
                      fieldType,
                    ],
                    TOperand(TConditionalUnion(choicesToType(choices)))
                  ),
              exported: 'visible',
              implicit: 'holeArg',
            },
            // Expressions from the `Conditional` plugin are used to define predicates instead of
            // JS in `value` property because this generated Function is `JSON.stringify`ed when
            // sent to SES, which strips all non-JSON values from the input Object.
            expression: formField.required
              ? _$('Conditional', 'Apply on ConditionalUnion')
              : _$('Conditional', 'Apply on Some ConditionalUnion'),
          };
        }
      });
    }
  }

  return result;
}

export function generateFormName({
  title,
  name,
  formNames,
}: {
  title: string;
  name: string;
  formNames: Array<string>;
}): {
  titleAndFormName: string;
  formName: string;
  formNames: Array<string>;
} {
  const titleFormKey = generateTitleFormName(title, name);
  let fullFormName = titleFormKey;

  fullFormName = increment(formNames, fullFormName);

  return {
    titleAndFormName: fullFormName,
    formName: fullFormName.split(': ')[1] as string,
    formNames: formNames.concat(fullFormName),
  };
}

export function generateTitleFormName(title: string, name: string) {
  return `${title}: ${name}`;
}
